import React, { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useCurrency } from '../../context/CurrencyContext';
import { CoinIcon } from './CoinIcon';
import { CoinTxnStat } from './coinTxnStats';
import { formatPercent } from '../../utils/formatters';

interface Props {
  coins: CoinTxnStat[];
  selectedCoinId: string | null;     // null = "All transactions"
  onSelect: (coinId: string | null) => void;
  totalCount: number;
  imageMap: Record<string, string>;
  /** Extra classes on the root (used for the mobile single-column drill-in). */
  className?: string;
}

const BRONZE = '#b89968';
const BRONZE_BRIGHT = '#d4b483';

export function TransactionsSidebar({
  coins,
  selectedCoinId,
  onSelect,
  totalCount,
  imageMap,
  className,
}: Props) {
  const { fmt } = useCurrency();
  const [query, setQuery] = useState('');

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return coins;
    return coins.filter(
      (c) => c.name.toLowerCase().includes(q) || c.symbol.toLowerCase().includes(q)
    );
  }, [coins, query]);

  const allActive = selectedCoinId === null;

  return (
    <div
      className={`flex-shrink-0 self-start ${className ?? ''}`}
      style={{
        width: 240,
        background: 'rgb(var(--overlay) / 0.015)',
        border: '0.5px solid rgb(var(--overlay) / 0.04)',
        borderRadius: 10,
        padding: 9,
      }}
    >
      {/* Search */}
      <div className="relative mb-2.5">
        <Search
          className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none"
          style={{ color: 'var(--color-sub)' }}
        />
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search coin..."
          className="w-full rounded-lg pl-9 pr-2.5 py-2 text-[13px] focus:outline-none"
          style={{
            background: 'rgb(var(--overlay) / 0.03)',
            border: '0.5px solid rgb(var(--overlay) / 0.07)',
            color: 'rgb(var(--sl-100))',
          }}
        />
      </div>

      {/* All transactions */}
      <button
        type="button"
        onClick={() => onSelect(null)}
        className="w-full flex items-center justify-between rounded-lg px-2.5 py-2.5 mb-3 transition-colors text-left"
        style={{
          background: allActive ? 'rgba(212,180,131,0.10)' : 'rgb(var(--overlay) / 0.025)',
          border: '0.5px solid rgb(var(--overlay) / 0.06)',
          borderLeft: allActive ? `2px solid ${BRONZE_BRIGHT}` : '0.5px solid rgb(var(--overlay) / 0.06)',
        }}
        onMouseEnter={(e) => { if (!allActive) e.currentTarget.style.background = 'rgb(var(--overlay) / 0.045)'; }}
        onMouseLeave={(e) => { if (!allActive) e.currentTarget.style.background = 'rgb(var(--overlay) / 0.025)'; }}
      >
        <span className="text-[13px] font-medium" style={{ color: 'rgb(var(--sl-100))' }}>
          All transactions
        </span>
        <span className="text-[12px] num" style={{ color: 'var(--color-sub)' }}>
          {totalCount}
        </span>
      </button>

      {/* Section label */}
      <div
        className="uppercase font-semibold px-1 mb-2"
        style={{ fontSize: 11, letterSpacing: '0.1em', color: BRONZE }}
      >
        Your coins
      </div>

      {/* Coin rows */}
      <div className="flex flex-col gap-0.5 max-h-[560px] overflow-y-auto -mr-1 pr-1">
        {filtered.length === 0 ? (
          <p className="text-[12px] px-1 py-3" style={{ color: 'var(--color-sub)' }}>
            {query ? 'No matching coins' : 'No coins yet'}
          </p>
        ) : (
          filtered.map((c) => {
            const active = c.coinId === selectedCoinId;
            const pnlColor =
              c.pnlPercent >= 0 ? 'var(--color-positive)' : 'var(--color-negative)';
            return (
              <button
                key={c.coinId}
                type="button"
                onClick={() => onSelect(c.coinId)}
                className="w-full flex items-center gap-2.5 rounded-lg px-2 py-2 transition-colors text-left"
                style={{
                  background: active ? 'rgba(212,180,131,0.08)' : 'transparent',
                  borderLeft: active ? `2px solid ${BRONZE_BRIGHT}` : '2px solid transparent',
                }}
                onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = 'rgb(var(--overlay) / 0.04)'; }}
                onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = 'transparent'; }}
              >
                <CoinIcon coinId={c.coinId} symbol={c.symbol} imageMap={imageMap} size={27} />
                <div className="min-w-0 flex-1">
                  <p className="text-[13px] font-medium truncate" style={{ color: 'rgb(var(--sl-100))' }}>
                    {c.name}
                  </p>
                  <p className="text-[11px] truncate" style={{ color: 'var(--color-sub)' }}>
                    {c.symbol} · {c.txnCount} txn{c.txnCount === 1 ? '' : 's'}
                  </p>
                </div>
                <div className="text-right flex-shrink-0">
                  <p className="text-[13px] num" style={{ color: 'rgb(var(--sl-100))' }}>
                    {fmt(c.value, true)}
                  </p>
                  {c.hasPnlPercent && (
                    <p className="text-[11px] num" style={{ color: pnlColor }}>
                      {formatPercent(c.pnlPercent)}
                    </p>
                  )}
                </div>
              </button>
            );
          })
        )}
      </div>
    </div>
  );
}
